import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { clearSelection } from "@redux/slices/tenantSlice";
import { clearUserProfile } from "@redux/slices/userProfileSlice";
import type { SelectionSnapshot, View } from "./viewsModel";

/**
 * The open views (project 13D). Persisted through `persist:root` so they survive a
 * reload; logout clears them twice over — `clearSelection` resets this slice, and the
 * logout then removes `persist:root` (intent I2). `ownerKey` is the belt to those
 * braces: views restored for a different user than the one signed in are dropped.
 *
 * The decisions (what "Add view" adds, where closing lands) live in `viewsModel`; the
 * reducers only apply them.
 */
export interface ViewsState {
  items: View[];
  activeId: string | null;
  ownerKey: string | null;
}

const initialState: ViewsState = { items: [], activeId: null, ownerKey: null };

const findView = (state: ViewsState, id: string) => state.items.find((v) => v.id === id);

const viewsSlice = createSlice({
  name: "views",
  initialState,
  reducers: {
    addViews(state, action: PayloadAction<View[]>) {
      const known = new Set(state.items.map((v) => v.id));
      state.items.push(...action.payload.filter((v) => !known.has(v.id)));
    },
    setActiveView(state, action: PayloadAction<string | null>) {
      const id = action.payload;
      state.activeId = id && findView(state, id) ? id : null;
    },
    removeView(state, action: PayloadAction<string>) {
      state.items = state.items.filter((v) => v.id !== action.payload);
      if (state.activeId === action.payload) state.activeId = null;
    },
    moveView(state, action: PayloadAction<{ id: string; toIndex: number }>) {
      const from = state.items.findIndex((v) => v.id === action.payload.id);
      if (from === -1) return;
      const to = Math.max(0, Math.min(action.payload.toIndex, state.items.length - 1));
      if (from === to) return;
      const [view] = state.items.splice(from, 1);
      state.items.splice(to, 0, view);
    },
    setViewUrl(state, action: PayloadAction<{ id: string; url: string }>) {
      const view = findView(state, action.payload.id);
      if (view && view.url !== action.payload.url) view.url = action.payload.url;
    },
    setViewSelection(state, action: PayloadAction<{ id: string; selection: SelectionSnapshot }>) {
      const view = findView(state, action.payload.id);
      if (view) view.selection = action.payload.selection;
    },
    setViewLabel(state, action: PayloadAction<{ id: string; label: string | null; labelFor: string }>) {
      const view = findView(state, action.payload.id);
      if (!view) return;
      view.label = action.payload.label;
      view.labelFor = action.payload.labelFor;
    },
    claimViews(state, action: PayloadAction<string>) {
      if (state.ownerKey === action.payload) return;
      // Someone else's views (or ones from before `ownerKey` existed with a different
      // user): never show them.
      if (state.ownerKey !== null) {
        state.items = [];
        state.activeId = null;
      }
      state.ownerKey = action.payload;
    },
    clearViews: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(clearSelection, () => initialState)
      .addCase(clearUserProfile, () => initialState);
  },
});

export const {
  addViews,
  setActiveView,
  removeView,
  moveView,
  setViewUrl,
  setViewSelection,
  setViewLabel,
  claimViews,
  clearViews,
} = viewsSlice.actions;

export default viewsSlice.reducer;
